// v0.6.10 — inline "set the rate category", per row or in bulk.
//
// MISSING_VAT_RATE_ON_DOMESTIC asks which category a domestic 0% row
// belongs to. Before this the only way to answer was to re-upload the
// whole file — disproportionate when 31 rows out of 951 are affected.
//
// A CATEGORY, deliberately, not a rate. The engine resolves the rate
// from the versioned table for each row's own country and date, so the
// answer stays right across rate changes and across countries. Offering
// "set them all to 20%" would invite clearing the queue by stamping 20%
// on rows that are genuinely zero-rated — over-declaring, which is the
// mirror of the under-declaration this flag exists to catch.
//
// Styling follows the standing requirement recorded for the FX
// "Save & re-run" button: an action that changes part of a calculation
// must NOT wear the primary-CTA teal, or an operator reasonably reads it
// as "redo everything". Hence the slate outline and the explicit
// "re-run needed" note rather than a big coloured button.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Warning } from "@phosphor-icons/react";
import { amendRateCategory } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { RATE_CATEGORY_REASONS } from "../../lib/reviewReasonPresets";

const CATEGORIES = [
  { value: "STANDARD", labelKey: "ui.rate_category.standard", fallback: "Standard" },
  { value: "REDUCED", labelKey: "ui.rate_category.reduced", fallback: "Reduced" },
  { value: "ZERO", labelKey: "ui.rate_category.zero", fallback: "Zero-rated" },
  { value: "EXEMPT", labelKey: "ui.rate_category.exempt", fallback: "Exempt" },
];

// A2-365 (2026-10-18) — the categories the rows' own countries define, with
// their rates ("Reduced: FR 5.5 %"), from the review group's rate_options.
// France and Italy each run three reduced rates; a bare "Reduced" could not
// say which, so it re-flagged on every re-run. Zero and Exempt stay offered.
const CATEGORY_LABEL = {
  STANDARD: "Standard", REDUCED: "Reduced", INTERMEDIATE: "Intermediate",
  SUPER_REDUCED: "Super-reduced", MINIMUM: "Minimum", PARKING: "Parking",
  REDUCED_5: "Reduced", REDUCED_13: "Reduced", REDUCED_18: "Reduced",
  ZERO: "Zero-rated", EXEMPT: "Exempt",
};
const pct = (r) => `${(Number(r) * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} %`;

export function categoriesFor(rateOptions) {
  if (!rateOptions || rateOptions.length === 0) return CATEGORIES;
  const byCat = new Map();
  for (const o of rateOptions) {
    if (o.category === "ZERO") continue;
    const parts = byCat.get(o.category) || [];
    parts.push(`${o.country} ${pct(o.rate)}`);
    byCat.set(o.category, parts);
  }
  const out = [...byCat.entries()].map(([value, parts]) => ({
    value, fallback: `${CATEGORY_LABEL[value] || value}: ${parts.join(", ")}`,
  }));
  return [...out, CATEGORIES[2], CATEGORIES[3]];
}

// A2-532 — with `txnIds` the pick applies to those rows only (the bulk bar's
// ticked rows); without, to the whole group, resolved server-side.
// A2-578/A2-579 (operator 2026-10-27): with `selectedIds` this is the group's
// ONE answer control — scope "all N rows in this group" or "only the rows I
// tick", the button saying exactly what it will do, sticky at the top.
export const RateCategoryAction = ({ calcId, flagType, groupCount, onDone, disabled, rateOptions, txnIds: givenIds,
  selectedIds = null, pageCount = 0, onSelectPage = null, onClearSelection = null,
  // A2-582: "all N rows" only when the server says every open row takes the
  // pick (g.bulk_answers); otherwise only the ticked rows.
  allowAll = true }) => {
  const options = categoriesFor(rateOptions);
  const { t } = useTranslation();
  const [cat, setCat] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const scoped = Array.isArray(selectedIds);
  const [scope, setScope] = useState(allowAll ? "all" : "ticked");
  const picked = scoped ? selectedIds.length : 0;

  // only rows the operator ticked (the bar's, or this control's own scope)
  const txnIds = scoped ? (scope === "ticked" ? selectedIds : null) : givenIds;
  const ticked = (txnIds || []).length;
  const nothingTicked = scoped && scope === "ticked" && picked === 0;
  const tp = givenIds ? "rate-category-bulk" : "rate-category";   // test ids, one picker per place
  const total = ticked || groupCount || 0;
  const valid = cat && reason.trim().length >= 10 && !/[<>]/.test(reason) && !nothingTicked;

  const submit = async () => {
    if (nothingTicked) return;   // never fall back to the whole group when "only the ticked rows" was chosen
    setBusy(true); setErr(null);
    try {
      const r = await amendRateCategory(calcId, {
        // the whole group, resolved server-side — the queue only hands
        // us the current page's ids (50), so sending those would
        // silently amend a fraction of a larger group. Ticked rows
        // (A2-532) are named explicitly instead.
        ...(ticked ? { txn_ids: txnIds } : { apply_to_all_flagged: true }),
        // only THIS group's rows: the server amends nothing outside it
        flag_type: flagType,
        rate_category: cat, reason: reason.trim(),
      });
      setDone(r);
      if (onDone) onDone(r);
    } catch (e) {
      setErr(errorMessage(e));
    } finally { setBusy(false); }
  };

  if (done) {
    // v0.6.10 fix — this said "re-run the calculation to apply it" and
    // gave no way to do it. The operator is on Step 4; the re-run lives
    // on Step 3. Found live: an operator set the category, saw the flag
    // unchanged (correctly — nothing re-runs on its own), and set it
    // again. Same dead end as the calc-level gate in 83e0fda: naming a
    // destination is not the same as offering it.
    //
    // react-router <Link>, never <a href> — a full page load drops the
    // in-progress workflow session (AddRegistrationCta.jsx / v0.6.8).
    return (
      <div
        data-testid={`${tp}-done`}
        className="mt-2 rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-2"
      >
        <div className="flex items-start gap-2 text-[11px] text-emerald-800 dark:text-emerald-200">
          <Check size={12} weight="bold" className="mt-0.5 shrink-0" />
          <span>
            {t("ui.review_grouped.rate_category.done", { count: done.amended, category: done.rate_category, defaultValue: `${done.amended} row(s) set to ${done.rate_category}. Saved. The VAT is worked out from the rate table when the calculation re-runs — give your other answers first, then re-run once (the bar at the top of this step).` })}
          </span>
        </div>
        <Link
          to="/workflow/calculate"
          data-testid={`${tp}-goto-rerun`}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg border border-emerald-700 dark:border-emerald-400 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
        >
          {t("ui.review_grouped.rate_category.goto_rerun", { defaultValue: "Go to Step 3 and re-run" })}
          <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid={`${tp}-action`}
      className={`mt-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-3 space-y-2 ${scoped ? "sticky top-16 z-20 shadow-md" : ""}`}
    >
      {scoped && (
        <div data-testid={`${tp}-scope`} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-800 dark:text-slate-100">
          <label className="flex items-center gap-1.5">
            <input type="radio" name={`scope-${flagType}`} checked={scope === "all"} onChange={() => setScope("all")}
                   disabled={!allowAll} data-testid={`${tp}-scope-all`} />
            {t("ui.review_grouped.rate_category.scope_all", { n: groupCount, defaultValue: `All ${groupCount} rows in this group` })}
          </label>
          <label className="flex items-center gap-1.5">
            <input type="radio" name={`scope-${flagType}`} checked={scope === "ticked"} onChange={() => setScope("ticked")}
                   data-testid={`${tp}-scope-ticked`} />
            {t("ui.review_grouped.rate_category.scope_ticked", { n: picked, defaultValue: `Only the rows I tick (${picked})` })}
          </label>
          {scope === "ticked" && onSelectPage && (
            <button type="button" className="underline text-xs" data-testid={`${tp}-select-page`} onClick={onSelectPage}>
              {t("ui.review_grouped.bulk_b_select_all", { page: pageCount, defaultValue: `Select all ${pageCount} on this page` })}
            </button>
          )}
          {scope === "ticked" && picked > 0 && onClearSelection && (
            <button type="button" className="underline text-xs text-slate-500" onClick={onClearSelection}>
              {t("ui.review_grouped.bulk_b_clear", { defaultValue: "Clear" })}
            </button>
          )}
        </div>
      )}
      <div className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300">
        {scoped ? null : ticked
          ? t("ui.review_grouped.rate_category.heading_ticked", { count: total, defaultValue: `Set the rate category for the ${total} ticked row(s)` })
          : t("ui.review_grouped.rate_category.heading", { count: total, defaultValue: `Set the rate category for all ${total} row(s) in this group` })}
      </div>
      <div className="flex flex-wrap gap-1">
        {options.map((c) => (
          <button
            key={c.value}
            type="button"
            data-testid={`${tp}-${c.value}`}
            onClick={() => setCat(c.value)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              cat === c.value
                ? "bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 border-slate-700 font-bold"
                : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-slate-500"
            }`}
          >
            {c.labelKey ? t(c.labelKey, { defaultValue: c.fallback }) : c.fallback}
          </button>
        ))}
      </div>
      {/* v0.6.12 A5-27 — pick, don't write. */}
      <PresetReasonSelect
        testid={`${tp}-reason-preset`}
        presets={RATE_CATEGORY_REASONS}
        preset={preset}
        onPick={(p, v) => { setPreset(p); setReason(v); }}
      />
      {err && (
        <div
          data-testid={`${tp}-error`}
          className="text-[11px] text-rose-700 dark:text-rose-300 flex items-start gap-1.5"
        >
          <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />{err}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {t("ui.review_grouped.rate_category.note", { defaultValue: "Records your answer — a calc re-run applies it. Your uploaded figures are not changed." })}
        </span>
        <button
          type="button"
          data-testid={`${tp}-apply`}
          disabled={busy || !valid || disabled}
          onClick={submit}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border ${
            (busy || !valid || disabled)
              ? "border-slate-300 dark:border-slate-700 text-slate-400 cursor-not-allowed"
              : "border-slate-700 dark:border-slate-300 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          {busy ? t("ui.review_grouped.rate_category.saving", { defaultValue: "Saving…" })
            : !scoped ? t("ui.review_grouped.rate_category.submit", { defaultValue: "Set category" })
            : scope === "all" ? t("ui.review_grouped.rate_category.apply_all", { n: groupCount, defaultValue: `Apply to ${groupCount} rows` })
            : t("ui.review_grouped.rate_category.apply_ticked", { n: picked, defaultValue: `Apply to ${picked} selected rows` })}
        </button>
      </div>
    </div>
  );
};

export default RateCategoryAction;
